import { last } from 'lodash-es';
import { dictEntity, type ticketDetails } from 'shared';
import type { Dict } from '@aviata/platform-types';
import { airflow } from '@aviata/platform-client';

import { convertBaggage } from '../convert-baggage';

/**
 * Gets point data (departure/arrival) for a segment
 * Shared function used by bloomreach-events and accelera-events
 * Stores original ISO datetime string - conversion happens before sending
 */
export const getPointData = (
  point: ticketDetails.types.MappedSegmentPoint,
  dict: Dict.Entity,
) => {
  const city = dictEntity.lib.getCityByAirport(point.airport, dict);
  return {
    // Original ISO datetime string with timezone (e.g. "2026-02-06T06:15:00+05:00")
    // Converted to Unix for bloomreach, to local time string for accelera
    at: point.at,
    city: city.name,
    city_ru: city.name_ru,
    city_kk: city.name_kk,
    city_en: city.name_en,
    iata: city.iata,
    country: {
      name: city.country?.name,
      name_ru: city.country?.name_ru,
      name_kk: city.country?.name_kk,
      name_en: city.country?.name_en,
      iso_code: city.country?.iso_code,
    },
  };
};

/**
 * Gets segment data for analytics events
 * Shared function used by bloomreach-events and accelera-events
 * Note: baggage and hand_luggage are only included if they have values (not empty/null)
 */
export const getSegmentData = (
  segment: ticketDetails.types.MappedSegment,
  dict: Dict.Entity,
) => {
  const baggage = convertBaggage(segment.baggage);
  const handLuggage = convertBaggage(segment.hand_luggage);

  const segmentData: {
    dep: ReturnType<typeof getPointData>;
    arr: ReturnType<typeof getPointData>;
    airline: { code: string; name: string };
    baggage?: string;
    hand_luggage?: string;
    cabin_class: string;
    stops_count: number;
    flight_number: string;
  } = {
    dep: getPointData(segment.dep, dict),
    arr: getPointData(segment.arr, dict),
    airline: {
      code: segment.display_airline,
      name: segment.airlineTitle,
    },
    cabin_class: segment.cabin.class.toLowerCase(),
    stops_count: segment.stops.count,
    flight_number: segment.flight_number,
  };

  // Only include baggage if it has a value (don't send if empty)
  if (baggage) {
    segmentData.baggage = baggage;
  }

  // Only include hand_luggage if it has a value (don't send if empty)
  if (handLuggage) {
    segmentData.hand_luggage = handLuggage;
  }

  return segmentData;
};

/**
 * Result type for segments data extraction
 */
export type SegmentsDataResult = {
  segments_data_from: ReturnType<typeof getSegmentData>[];
  segments_data_to?: ReturnType<typeof getSegmentData>[];
  // ISO datetime strings - converted before sending
  from_dep: string;
  from_arr: string;
  to_dep?: string;
  to_arr?: string;
};

/**
 * Gets segments data from mapped flights array
 * Returns segments_data_from and optionally segments_data_to for round trips
 * Stores original ISO datetime strings - conversion happens before sending
 *
 * @param mappedFlights - Array of mapped flights (from bookingStore.mappedFlights)
 * @param dict - Dictionary for city/airport data
 * @param searchQuery - Search query string to determine if RT
 */
export const getSegmentsDataFromMappedFlights = (
  mappedFlights: ticketDetails.types.MappedFlight[],
  dict: Dict.Entity,
  searchQuery: string,
): SegmentsDataResult => {
  const segmentsDataFrom = mappedFlights[0].segments.map(s =>
    getSegmentData(s, dict),
  );

  const result: SegmentsDataResult = {
    segments_data_from: segmentsDataFrom,
    from_dep: segmentsDataFrom[0].dep.at,
    from_arr: last(segmentsDataFrom).arr.at,
  };

  const searchParams = airflow.utils.parseQuery(searchQuery);

  if (searchParams?.RT && mappedFlights[1]) {
    const segmentsDataTo = mappedFlights[1].segments.map(s =>
      getSegmentData(s, dict),
    );
    result.segments_data_to = segmentsDataTo;
    result.to_dep = segmentsDataTo[0].dep.at;
    result.to_arr = last(segmentsDataTo).arr.at;
  }

  if (searchParams?.ML) {
    const lastFlight = mappedFlights[mappedFlights.length - 1];
    if (lastFlight && mappedFlights.length > 1) {
      const segmentsDataTo = lastFlight.segments.map(s =>
        getSegmentData(s, dict),
      );
      result.segments_data_to = segmentsDataTo;
      result.to_dep = segmentsDataTo[0].dep.at;
      result.to_arr = last(segmentsDataTo).arr.at;
    }
  }

  return result;
};

/**
 * Checks if a baggage/hand_luggage string represents actual baggage
 * (not "0 PC", "0 KG", or empty/null)
 * Uses the same logic as offerPriceClick hasBaggage check
 */
const isActualBaggage = (item: string | undefined): boolean =>
  !!item && !item.includes('0');

/**
 * Computes has_baggage and has_hand_luggage from segments data
 * Used to get up-to-date values after fare family change
 *
 * @param segmentsData - Current segments data (from bookingStore after fare family change)
 * @returns Object with has_baggage and has_hand_luggage booleans
 */
export const getBaggageFlagsFromSegmentsData = (
  segmentsData: SegmentsDataResult,
): { has_baggage: boolean; has_hand_luggage: boolean } => {
  const allSegments = [
    ...segmentsData.segments_data_from,
    ...(segmentsData.segments_data_to ?? []),
  ];

  return {
    has_baggage: allSegments.some(s => isActualBaggage(s.baggage)),
    has_hand_luggage: allSegments.some(s => isActualBaggage(s.hand_luggage)),
  };
};

/**
 * Gets segments data for all flights in an offer
 * Returns segments_data_from and optionally segments_data_to for round trips
 * Stores original ISO datetime strings - conversion happens before sending
 */
export const getSegmentsDataFromOffer = (
  offer: ticketDetails.types.MappedOffer,
  dict: Dict.Entity,
) => {
  return getSegmentsDataFromMappedFlights(
    offer.flights,
    dict,
    offer.$meta.search_query,
  );
};
